import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu as MenuIcon, X, ShoppingBag, Facebook, Instagram } from "lucide-react";
import { BRAND, SOCIALS } from "../data/restaurant";
import { useCart } from "../context/CartContext";
import logo from "../assets/pattista-logo.png";

const SECTION_LINKS = [
  { label: "Home", href: "#top" },
  { label: "Gallery", href: "#gallery" },
  { label: "About Us", href: "#about" },
  { label: "Contact", href: "#contact" },
  { label: "Delivery Partners", href: "#delivery" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const goSection = (e, href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/", { state: { scrollTo: href } });
    }
  };

  const goMenu = (e) => {
    setOpen(false);
    if (location.pathname === "/menu") {
      e.preventDefault();
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
  };

  const openCart = () => {
    setOpen(false);
    setCartOpen(true);
  };

  const cartButton = (testid) => (
    <button
      type="button"
      data-testid={testid}
      onClick={openCart}
      aria-label={`Open cart, ${count} item${count === 1 ? "" : "s"}`}
      className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-jet bg-cream text-jet shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard"
    >
      <ShoppingBag className="h-5 w-5" aria-hidden="true" />
      {count > 0 && (
        <span
          data-testid="cart-count-badge"
          className="absolute -right-1.5 -top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-jet bg-crimson px-1 font-display text-xs font-black text-cream"
        >
          {count}
        </span>
      )}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 border-b-[3px] border-jet bg-amber/90 backdrop-blur-md">
      <nav className="mx-auto flex h-[76px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6" aria-label="Main">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => goSection(e, "#top")}
          className="flex shrink-0 items-center gap-2"
          aria-label="Pattista home"
        >
          <img src={logo} alt="Pattista logo" className="h-12 w-12 rounded-full border-2 border-jet bg-cream object-cover shadow-hard-sm sm:h-14 sm:w-14" />
          <span className="font-display text-xl font-black tracking-tight text-jet">PATTISTA</span>
        </a>

        <div className="hidden items-center gap-6 lg:flex">
          {SECTION_LINKS.slice(0, 1).map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid="nav-link-top"
              onClick={(e) => goSection(e, l.href)}
              className="font-display text-sm font-extrabold uppercase tracking-wide text-jet transition-transform duration-200 hover:-rotate-2 hover:text-crimson"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/menu"
            data-testid="nav-link-menu"
            onClick={goMenu}
            className="font-display text-sm font-extrabold uppercase tracking-wide text-jet transition-transform duration-200 hover:-rotate-2 hover:text-crimson"
          >
            Menu
          </Link>
          {SECTION_LINKS.slice(1).map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={(e) => goSection(e, l.href)}
              className="font-display text-sm font-extrabold uppercase tracking-wide text-jet transition-transform duration-200 hover:-rotate-2 hover:text-crimson"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          {SOCIALS.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Pattista on ${s.name}`}
              data-testid={s.testid}
              className="hidden h-10 w-10 items-center justify-center rounded-full border-[3px] border-jet bg-cream text-jet shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-sunshine md:inline-flex"
            >
              {s.icon === "facebook" ? <Facebook className="h-4 w-4" aria-hidden="true" /> : <Instagram className="h-4 w-4" aria-hidden="true" />}
            </a>
          ))}
          {cartButton("cart-button")}
          <a
            href={BRAND.phoneHref}
            data-testid="nav-cta-call-button"
            className="group hidden items-center gap-2 rounded-full border-[3px] border-jet bg-crimson px-5 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-cream shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard md:inline-flex"
          >
            <Phone className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" aria-hidden="true" />
            Call to Order
          </a>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border-[3px] border-jet bg-cream shadow-hard-sm lg:hidden"
          >
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t-[3px] border-jet bg-amber lg:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              <a
                href="#top"
                data-testid="nav-mobile-link-top"
                onClick={(e) => goSection(e, "#top")}
                className="rounded-xl px-3 py-3 font-display text-lg font-extrabold uppercase text-jet hover:bg-cream"
              >
                Home
              </a>
              <Link
                to="/menu"
                data-testid="nav-mobile-link-menu"
                onClick={goMenu}
                className="rounded-xl px-3 py-3 font-display text-lg font-extrabold uppercase text-jet hover:bg-cream"
              >
                Menu
              </Link>
              {SECTION_LINKS.slice(1).map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={(e) => goSection(e, l.href)}
                  className="rounded-xl px-3 py-3 font-display text-lg font-extrabold uppercase text-jet hover:bg-cream"
                >
                  {l.label}
                </a>
              ))}
              <button
                type="button"
                data-testid="nav-mobile-cart-button"
                onClick={openCart}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-jet px-5 py-3 font-display text-base font-extrabold uppercase text-cream shadow-hard-flame"
              >
                <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                View Cart ({count})
              </button>
              <a
                href={BRAND.phoneHref}
                data-testid="nav-mobile-cta-call-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-crimson px-5 py-3 font-display text-base font-extrabold uppercase text-cream shadow-hard-sm"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                Call {BRAND.phoneDisplay}
              </a>
              <div className="mt-3 flex items-center justify-center gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Pattista on ${s.name}`}
                    data-testid={`nav-mobile-${s.testid}`}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-jet bg-cream text-jet shadow-hard-sm"
                  >
                    {s.icon === "facebook" ? <Facebook className="h-4 w-4" aria-hidden="true" /> : <Instagram className="h-4 w-4" aria-hidden="true" />}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
